/**
 * Data Upload Manager templates — aligned with reference DataUploadManager.
 * Account-team and customer-team share the same CSV column layout.
 */

import {
  ACCOUNT_TEAM_ALLOWED_ROLES,
  CUSTOMER_TEAM_ALLOWED_ROLES,
} from "./RoleConstants";

export type TeamUploadType =
  | "upload_account_team_members"
  | "upload_customer_team_members";

/** Shared CSV columns for account-team and customer-team uploads */
export const TEAM_MEMBER_TEMPLATE_COLUMNS = [
  "account_name",
  "account_id",
  "member_name",
  "member_title",
  "member_email",
  "member_phone",
  "member_languages",
  "role",
] as const;

/** Required columns (member_email validated separately in row validation) */
export const TEAM_MEMBER_REQUIRED_COLUMNS = [
  "account_name",
  "account_id",
  "member_name",
  "member_title",
  "role",
] as const;

export { ACCOUNT_TEAM_ALLOWED_ROLES, CUSTOMER_TEAM_ALLOWED_ROLES };

export const getTeamTemplateColumns = (): readonly string[] =>
  TEAM_MEMBER_TEMPLATE_COLUMNS;

export const getTeamRequiredColumns = (): readonly string[] =>
  TEAM_MEMBER_REQUIRED_COLUMNS;

export const buildTeamMemberSampleRow = (
  account: { customer_id: string; name: string },
  type: TeamUploadType,
  options: { includeSampleMember?: boolean; randomSuffix?: string } = {}
): Record<string, string> => {
  const { includeSampleMember = false, randomSuffix = "sample" } = options;

  const base = {
    account_name: account.name,
    account_id: account.customer_id,
    member_name: "",
    member_title: "",
    member_email: "",
    member_phone: "",
    member_languages: "",
    role: "",
  };

  if (!includeSampleMember) return base;

  if (type === "upload_account_team_members") {
    return {
      ...base,
      member_name: "Jane Doe",
      member_title: "Senior Account Executive",
      member_email: `jane.doe.${randomSuffix}@izba.ai`,
      member_phone: "+1-555-0100",
      member_languages: "English, Spanish",
      role: "AE",
    };
  }

  return {
    ...base,
    member_name: "Katy Bell",
    member_title: "Customer Contact",
    member_email: `katy.bell.${randomSuffix}@client.com`,
    member_phone: "+1-555-0200",
    member_languages: "English",
    role: "Economic Buyer",
  };
};

/** Reference sample rows when no accounts are selected yet */
export const ACCOUNT_TEAM_STATIC_SAMPLES: Record<string, string>[] = [
  {
    account_name: "Acme Corporation",
    account_id: "",
    member_name: "Jane Doe",
    member_title: "Senior Account Executive",
    member_email: "jane.doe@acme.com",
    member_phone: "+1-555-0100",
    member_languages: "English, Spanish",
    role: "AE",
  },
];

export const CUSTOMER_TEAM_STATIC_SAMPLES: Record<string, string>[] = [
  {
    account_name: "Acme Corporation",
    account_id: "",
    member_name: "Katy Bell",
    member_title: "Customer Contact",
    member_email: "katy.bell@acme.com",
    member_phone: "+1-555-0200",
    member_languages: "English",
    role: "Economic Buyer",
  },
];
